/**
 * Design Check 3D viewer.
 *
 * Shows the uploaded part standing on a build plate in the currently selected
 * orientation, with a live overhang heatmap (green/amber/red vertex colours
 * recomputed from face normals whenever the orientation changes) and marker
 * spheres for the report's bridging spans and thin-wall hotspots.
 *
 * Z is up, matching the DFM analysis convention. Orbit controls follow the
 * custom drag/wheel pattern used by PartAlignment.jsx.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { bindViewerBackground } from '../../utils/theme';

const COLOR_OK = new THREE.Color(0x8fa3b8);
const COLOR_WARN = new THREE.Color(0xf59e0b);
const COLOR_FAIL = new THREE.Color(0xef4444);
const DEG = 180 / Math.PI;

function orientationMatrix(r) {
  // Row-major 3x3 from the report → THREE.Matrix4
  const m = new THREE.Matrix4();
  m.set(
    r[0][0], r[0][1], r[0][2], 0,
    r[1][0], r[1][1], r[1][2], 0,
    r[2][0], r[2][1], r[2][2], 0,
    0, 0, 0, 1
  );
  return m;
}

function Viewer3D({ positions, orientation, thresholds, envelopeMm, markers, overlay }) {
  const containerRef = useRef(null);
  const stateRef = useRef({});
  const orbitRef = useRef({ theta: 0.9, phi: 1.05, dist: 100, target: new THREE.Vector3() });

  // ── scene bootstrap (once per mesh) ──────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !positions) return;

    const w = container.clientWidth || 800;
    const h = container.clientHeight || 520;

    const scene = new THREE.Scene();
    const unbindBg = bindViewerBackground(scene, 0x101018, THREE);

    const camera = new THREE.PerspectiveCamera(45, w / h, 1, 1000000);
    camera.up.set(0, 0, 1);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(1, 1.4, 2.2);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x99aaff, 0.35);
    fill.position.set(-2, -1, 0.5);
    scene.add(fill);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(positions.length), 3));
    geo.computeVertexNormals();
    geo.computeBoundingBox();

    const mat = new THREE.MeshPhongMaterial({
      vertexColors: true,
      specular: 0x111111,
      shininess: 30,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(geo, mat);

    const group = new THREE.Group(); // orientation rotation + grounding happen here
    group.add(mesh);
    scene.add(group);

    const markerGroup = new THREE.Group();
    group.add(markerGroup);

    // Build plate sized to the part footprint
    const size = new THREE.Vector3();
    geo.boundingBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const plateSize = maxDim * 2.2;
    const grid = new THREE.GridHelper(plateSize, 22, 0x3a3a55, 0x22222f);
    grid.rotation.x = Math.PI / 2; // GridHelper is XZ by default; we want XY (Z up)
    scene.add(grid);

    const plateGeo = new THREE.PlaneGeometry(plateSize, plateSize);
    const plateMat = new THREE.MeshBasicMaterial({ color: 0x14141d, transparent: true, opacity: 0.85 });
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.position.z = -0.5;
    scene.add(plate);

    // Fade the plate edges out gently, well beyond the part itself
    scene.fog = new THREE.Fog(0x101018, maxDim * 4, maxDim * 12);

    orbitRef.current.dist = maxDim * 2.1;
    orbitRef.current.target.set(0, 0, size.z * 0.4);

    const updateCamera = () => {
      const { theta, phi, dist, target } = orbitRef.current;
      camera.position.set(
        target.x + dist * Math.sin(phi) * Math.cos(theta),
        target.y + dist * Math.sin(phi) * Math.sin(theta),
        target.z + dist * Math.cos(phi)
      );
      camera.lookAt(target);
    };

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      updateCamera();
      renderer.render(scene, camera);
    };
    animate();

    // drag orbit
    let dragging = false;
    let last = [0, 0];
    const onDown = (e) => { dragging = true; last = [e.clientX, e.clientY]; };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - last[0];
      const dy = e.clientY - last[1];
      last = [e.clientX, e.clientY];
      orbitRef.current.theta -= dx * 0.006;
      orbitRef.current.phi = Math.min(Math.PI - 0.05, Math.max(0.05, orbitRef.current.phi - dy * 0.006));
    };
    const onUp = () => { dragging = false; };
    const onWheel = (e) => {
      e.preventDefault();
      orbitRef.current.dist *= e.deltaY > 0 ? 1.1 : 0.9;
      orbitRef.current.dist = Math.max(maxDim * 0.3, Math.min(maxDim * 8, orbitRef.current.dist));
    };
    renderer.domElement.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    const onResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      if (!nw || !nh) return;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    stateRef.current = { scene, camera, renderer, mesh, geo, group, markerGroup, maxDim };

    return () => {
      unbindBg();
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      renderer.domElement.removeEventListener('mousedown', onDown);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.dispose();
      geo.dispose();
      mat.dispose();
      plateGeo.dispose();
      plateMat.dispose();
      container.innerHTML = '';
      stateRef.current = {};
    };
  }, [positions]);

  // ── orientation + heatmap + markers ──────────────────────────────────────
  const applyOrientation = useCallback(() => {
    const { geo, group, markerGroup } = stateRef.current;
    if (!geo || !orientation) return;
    const r = orientation.rotation;

    // Rotate the group, then ground it: bbox centre → origin, min z → plate
    group.setRotationFromMatrix(orientationMatrix(r));
    group.position.set(0, 0, 0);
    group.updateMatrixWorld(true);
    const worldBox = new THREE.Box3().setFromObject(group);
    const centre = new THREE.Vector3();
    worldBox.getCenter(centre);
    group.position.set(-centre.x, -centre.y, -worldBox.min.z);
    orbitRef.current.target.set(0, 0, (worldBox.max.z - worldBox.min.z) * 0.4);

    // Heatmap: per-face overhang class in the selected orientation
    const pos = geo.attributes.position.array;
    const col = geo.attributes.color.array;
    const warnDeg = (thresholds && thresholds.overhangWarnDeg) || 35;
    const failDeg = (thresholds && thresholds.overhangFailDeg) || 50;
    const showHeat = overlay === 'overhangs';

    const a = new THREE.Vector3(); const b = new THREE.Vector3(); const c = new THREE.Vector3();
    const e1 = new THREE.Vector3(); const e2 = new THREE.Vector3(); const n = new THREE.Vector3();

    for (let t = 0; t < pos.length / 9; t++) {
      const o = t * 9;
      a.set(pos[o], pos[o + 1], pos[o + 2]);
      b.set(pos[o + 3], pos[o + 4], pos[o + 5]);
      c.set(pos[o + 6], pos[o + 7], pos[o + 8]);
      e1.subVectors(b, a);
      e2.subVectors(c, a);
      n.crossVectors(e1, e2).normalize();
      const nz = r[2][0] * n.x + r[2][1] * n.y + r[2][2] * n.z;

      let colour = COLOR_OK;
      if (showHeat && nz < 0) {
        const angle = Math.asin(Math.min(1, -nz)) * DEG;
        if (angle > failDeg) colour = COLOR_FAIL;
        else if (angle > warnDeg) colour = COLOR_WARN;
      }
      for (let v = 0; v < 3; v++) {
        col[o + v * 3] = colour.r;
        col[o + v * 3 + 1] = colour.g;
        col[o + v * 3 + 2] = colour.b;
      }
    }
    geo.attributes.color.needsUpdate = true;

    // Markers (report coordinates are in original part space — same space as
    // the mesh — so adding them inside the rotated group lines them up).
    while (markerGroup.children.length) {
      const child = markerGroup.children.pop();
      child.geometry.dispose();
      child.material.dispose();
      markerGroup.remove(child);
    }
    if (overlay === 'issues' && markers && markers.length) {
      const radius = stateRef.current.maxDim * 0.015;
      for (const marker of markers) {
        const sphereGeo = new THREE.SphereGeometry(radius, 14, 14);
        const sphereMat = new THREE.MeshBasicMaterial({
          color: marker.kind === 'bridge' ? 0xef4444 : 0xf59e0b,
          transparent: true,
          opacity: 0.85,
          depthTest: false
        });
        const sphere = new THREE.Mesh(sphereGeo, sphereMat);
        sphere.renderOrder = 999;
        sphere.position.set(marker.p[0], marker.p[1], marker.p[2]);
        markerGroup.add(sphere);
      }
    }
  }, [orientation, thresholds, overlay, markers]);

  useEffect(() => { applyOrientation(); }, [applyOrientation, positions]);

  return <div ref={containerRef} className="dc-viewer-canvas" />;
}

export default Viewer3D;
